import type {AxiosInstance} from 'axios';
import type {
    ChartData,
    CommonListArgs,
    Country,
    GetEntityByIdParams,
    IsoDate,
    Nullable,
    PaginatedList, SortDirection,
} from './types.ts';
import type {CourseSummary} from './courses-api.ts';
import type {CreateAndEditPartnerDto} from '../assets/forms/form-schemes.ts';
import {prepareOrderingForBackend} from '../assets/helpers/utils/prepare-ordering-for-backend.ts';
import {handleAddPhotoToFormData} from '../assets/helpers/utils/handle-add-photo-to-form-data.ts';

export class PartnersApi {
    constructor(private instance: AxiosInstance) {
    }

    public async getList(params: PartnersListArgs) {
        return this.instance.get<PaginatedList<Partner>>('/partners/', {
            params: {
                page: params.page,
                search: params.search,
                ordering: prepareOrderingForBackend(params.sortBy, params.sortDirection),
                region_id: params.region_id,
            },
        }).then(res => res.data);
    };

    public async getById(params: GetEntityByIdParams) {
        return this.instance.get<PartnerDetail>(`/partners/${params.id}`).then(res => res.data);
    };

    public async getPerformance() {
        return this.instance.get<PartnerPerformance>('/partners/performance/').then(res => res.data);
    };

    public async createPartner(dto: CreateAndEditPartnerDto) {
        return this.instance.post<PartnerCreate>('/partners/', this.prepareFormDataToCreateAndEditPartner(dto)).then(res => res.data);
    }

    public async updatePartner(dto: PartnerUpdate) {
        return this.instance.patch<PartnerCreate>(`/partners/${dto.id}/`, this.prepareFormDataToCreateAndEditPartner(dto)).then(res => res.data);
    }

    public async deletePartner(dto: GetEntityByIdParams) {
        return this.instance.delete<PartnerCreate>(`/partners/${dto.id}/`).then(res => res.data);
    }

    private prepareFormDataToCreateAndEditPartner(dto: CreateAndEditPartnerDto) {
        const formData = new FormData();
        formData.append('legal_name', dto.legal_name);
        formData.append('country', String(dto.country));
        formData.append('partner_id', dto.partner_id);
        handleAddPhotoToFormData({
            formData,
            value: dto?.logo,
            propName: 'logo'
        })
        return formData;
    }
}

export type PartnerUpdate = CreateAndEditPartnerDto & GetEntityByIdParams;

export type PartnerCreate = {
    id: number;
    legal_name: string;
    country: number;
    partner_id: string;
    logo: string;
}

export type PartnersListArgs = {
    search?: string;
    sortBy?: string;
    sortDirection?: SortDirection;
    region_id?: string;
} & CommonListArgs;

export type Partner = {
    id: string
    legal_name: string
    country: Nullable<Country>
    logo: Nullable<string>
    business_address: string;
    tax_id: Nullable<string>
    contact_email: Nullable<string>
    contact_phone: Nullable<string>
    revenue_share_percentage: string
    preferred_currency: Nullable<string>
    is_active: boolean
    created_at: IsoDate;
    updated_at: IsoDate;
    devices_count: number
    courses_count: number;
    total_revenue: number;
    games_played: number;
    revenue_chart: ChartData[]
    games_played_chart: ChartData[]
}
export type PartnerRevenueGrossProfitPoint = {
    date: IsoDate
    total_revenue: number
    gross_profit: number
}
export type PartnerDetail = {
    id: string
    partner_id: string
    legal_name: string
    country: Nullable<Country>
    logo: Nullable<string>
    business_address: string
    tax_id: Nullable<string>
    contact_email: Nullable<string>
    contact_phone: Nullable<string>
    revenue_share_percentage: number
    preferred_currency: Nullable<string>
    is_active: boolean
    total_revenue: number
    gross_profit: number
    club_profit: number
    created_at: IsoDate
    updated_at: IsoDate
    revenue_gross_profit_chart: PartnerRevenueGrossProfitPoint[]
    courses: PartnerCourse[]
}

export type PartnerCourse = {
    id: string;
    name: string;
    address: string;
    igolf_course: Nullable<PartnerIGolfCourse>;
    geometry: unknown;
    devices: number
    summary: CourseSummary;
}

export type PartnerIGolfCourse = {
    id_course: string;
    course_name: string;
    latitude: Nullable<string>;
    longitude: Nullable<string>;
    layout_holes: Nullable<number>;
    layout_total_holes: Nullable<number>;
    gps_available: boolean;
    address?: Nullable<string>;
    address1?: Nullable<string>;
    address2?: Nullable<string>;
    city?: Nullable<string>;
}

export type PartnerPerformancePeriod = {
    from: string;
    to: string;
}

export type PartnerPerformanceTopPartner = {
    affiliate_id: number;
    legal_name: string;
    total_wager_amount: number;
    completed_sessions: number;
    courses_count: number;
    holes_count: number;
    driving_ranges_count: number;
    currency_code: string;
    currency_symbol: string;
    imageSrc?: string;
}

export type PartnerPerformanceSeriesItem = {
    date: string;
    total_revenue: number;
}

export type PartnerPerformance = {
    period: PartnerPerformancePeriod;
    revenue_series: PartnerPerformanceSeriesItem[];
    top_partners: PartnerPerformanceTopPartner[];
    total_revenue: number;
}
